import { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { toast } from 'sonner';
import { Upload, RotateCcw, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getSiteImageSlots, putSiteImage, resetSiteImage,
  uploadToCloudinary, getCloudinaryStatus, getCloudinarySignature,
} from '@/lib/api';
import { useSiteImagesContext } from '@/contexts/SiteImagesContext';

const SiteImagesManagement = () => {
  const { images, refresh } = useSiteImagesContext();
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState({}); // { slotKey: 'uploading' | 'resetting' }
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);

  useEffect(() => {
    // One-time boot fetches. No dependency loop because setters are stable.
    getSiteImageSlots()
      .then((r) => setSlots(r.data || []))
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.error('getSiteImageSlots failed:', err);
        setSlots([]);
      });
    getCloudinaryStatus()
      .then((r) => setCloudinaryEnabled(!!r.data?.configured))
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.error('getCloudinaryStatus failed:', err);
      });
  }, []);

  const handleUpload = async (slotKey, file) => {
    if (!file) return;
    if (!cloudinaryEnabled) {
      toast.error('Cloudinary is not configured — cannot upload.');
      return;
    }
    setBusy((b) => ({ ...b, [slotKey]: 'uploading' }));
    try {
      // Same compression pipeline as property images — 2400px max, ~400KB.
      let prepared = file;
      if (file.size > 400 * 1024 && file.type.startsWith('image/')) {
        try {
          prepared = await imageCompression(file, {
            maxSizeMB: 0.6, maxWidthOrHeight: 2400, useWebWorker: true,
            fileType: 'image/jpeg', initialQuality: 0.85,
          });
        } catch (compErr) {
          // Non-fatal: fall through to the original file so the upload still happens.
          if (process.env.NODE_ENV !== 'production') console.warn('Compression skipped:', file.name, compErr);
        }
      }
      const { data: sig } = await getCloudinarySignature('image', 'site');
      const result = await uploadToCloudinary(prepared, 'site', sig);
      if (!result?.secure_url) throw new Error(result?.error?.message || 'Upload failed');
      await putSiteImage(slotKey, { url: result.secure_url, alt: '' });
      await refresh();
      toast.success('Image updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[slotKey]; return c; });
    }
  };

  const handleReset = async (slotKey) => {
    if (!window.confirm('Revert this image to the default? The current custom upload will be removed.')) return;
    setBusy((b) => ({ ...b, [slotKey]: 'resetting' }));
    try {
      await resetSiteImage(slotKey);
      await refresh();
      toast.success('Reset to default');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Reset failed');
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[slotKey]; return c; });
    }
  };

  // Group slots by their `group` field so the panel renders sections cleanly.
  const grouped = slots.reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="site-images-management">
      <div>
        <h2 className="font-heading text-2xl text-charcoal">Site Images</h2>
        <p className="text-sm text-charcoal-muted mt-1">
          Swap the images shown on public pages. Each slot uploads to Cloudinary and updates live — no re-deploy needed.
        </p>
      </div>

      {Object.entries(grouped).map(([groupName, groupSlots]) => (
        <section key={groupName}>
          <h3 className="text-xs uppercase tracking-[0.2em] text-charcoal-muted mb-4">{groupName}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {groupSlots.map((slot) => {
              const current = images[slot.key];
              const url = current?.url || slot.default_url;
              const isCustom = current?.is_custom;
              const isBusy = busy[slot.key];
              return (
                <div
                  key={slot.key}
                  className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm"
                  data-testid={`site-image-slot-${slot.key}`}
                >
                  <div className="aspect-video bg-warm-sand relative">
                    {url ? (
                      <img src={url} alt={slot.label} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-charcoal-muted">
                        No image
                      </div>
                    )}
                    {isCustom && (
                      <span className="absolute top-2 right-2 bg-ubud-jungle text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" /> Custom
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="text-sm font-medium text-charcoal leading-snug">{slot.label}</div>
                    <div className="text-[11px] text-charcoal-muted mt-0.5 font-mono truncate">{slot.key}</div>
                    <div className="flex items-center gap-2 mt-3">
                      <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${isBusy ? 'bg-warm-sand text-charcoal-muted cursor-wait' : 'bg-ubud-jungle text-white hover:bg-ubud-jungle-dark'}`}>
                        {isBusy === 'uploading' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {isBusy === 'uploading' ? 'Uploading…' : 'Upload'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={!!isBusy}
                          onChange={(e) => handleUpload(slot.key, e.target.files?.[0])}
                          data-testid={`upload-${slot.key}`}
                        />
                      </label>
                      {isCustom && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!!isBusy}
                          onClick={() => handleReset(slot.key)}
                          className="text-xs h-8"
                          data-testid={`reset-${slot.key}`}
                        >
                          {isBusy === 'resetting' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                          Reset
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default SiteImagesManagement;
